import { useNavigate } from 'react-router-dom';
import { APPROVAL_MINE_ROLES, APPROVAL_QUEUE_ROLES, hasAnyOpsRole } from '../../auth/opsRoles.ts';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import {
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../ui/index.ts';
import { MineTab } from './MineTab.tsx';
import { QueueTab } from './QueueTab.tsx';

/**
 * Approval center shell (epic #1030 E1-S3, #1039) — the daily surface
 * for the "权限外先审批" principle. Split into `src/pages/approvals/`
 * in E3-G2 (#1103, #1098 deferred item 18): this shell owns the tabs
 * and role gating; `QueueTab` / `MineTab` own their views and
 * `kindPresentation.ts` owns the per-kind labels + payload summaries.
 *
 * Two tabs, deep-linkable as routes (`/approvals/queue`,
 * `/approvals/mine`) per the Tabs guidance:
 *   - Queue — approvers only (`ops-sales-lead`, `ops-finance-manager`,
 *     admin). Pending requests of the session's brand + approvable
 *     kinds; approve / reject with a MANDATORY note on reject.
 *   - My submissions — any sales role; row-level requester scope is
 *     enforced server-side. Each row expands into the status timeline
 *     derived from the request record.
 *
 * Dispatch Deck only: every visual comes from `src/ui/`; zero hex.
 */

export type ApprovalCenterTab = 'queue' | 'mine';

interface Props {
  readonly tab: ApprovalCenterTab;
}

export function ApprovalCenterPage({ tab }: Props): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useSession();
  const roles = user?.roles ?? [];
  const canSeeQueue = hasAnyOpsRole(roles, APPROVAL_QUEUE_ROLES);
  const canSeeMine = hasAnyOpsRole(roles, APPROVAL_MINE_ROLES);

  return (
    <section className="flex flex-col gap-4" data-testid="approval-center-page">
      <PageHeader title={t('approvals.title')} sub={t('approvals.subtitle')} />
      <Tabs
        value={tab}
        onValueChange={(value) => {
          navigate(value === 'mine' ? '/approvals/mine' : '/approvals/queue');
        }}
      >
        <TabsList ariaLabel={t('approvals.tabs.aria_label')}>
          {canSeeQueue ? (
            <TabsTrigger value="queue" testId="approvals-tab-queue">
              {t('approvals.tab.queue')}
            </TabsTrigger>
          ) : null}
          {canSeeMine ? (
            <TabsTrigger value="mine" testId="approvals-tab-mine">
              {t('approvals.tab.mine')}
            </TabsTrigger>
          ) : null}
        </TabsList>
        <TabsContent value="queue" className="pt-4">
          {canSeeQueue ? <QueueTab /> : null}
        </TabsContent>
        <TabsContent value="mine" className="pt-4">
          {canSeeMine ? <MineTab /> : null}
        </TabsContent>
      </Tabs>
    </section>
  );
}
